import type { RtiComparisonGroupModel } from 'model/interfaces/rti-comparison-group';
import type { RtiFieldLeaf, RtiFieldSection } from 'model/interfaces/rti-employee-data-fields';
import { humanizeRtiFieldKey, isCustomFieldValue, tryCoerceLooseRtiObjectLeaf } from 'utilities/rti-employee-data-fields';

const DEFAULT_TRUE_JOB_COMPANY_KEYS = [
  'company',
  'job_code',
  'job_level_grade',
  'dept_function',
] as const;

const DEFAULT_TRUE_JOB_COMPANY_KEY_SET = new Set<string>(DEFAULT_TRUE_JOB_COMPANY_KEYS);

const JOB_COMPANY_CANONICAL_BOOLEANS = [
  'company',
  'job_code',
  'job_group',
  'subfunction',
  'dept_function',
  'function_grade',
  'job_level_grade',
  'subfunction_grade',
] as const;

const JOB_COMPANY_CUSTOM_FIELD_KEYS = [
  'custom_field_1',
  'custom_field_2',
  'custom_field_3',
  'custom_field_4',
  'custom_field_5',
] as const;

function fillMissingJobCompanyLeaves(section: RtiFieldSection): RtiFieldSection {
  const out: RtiFieldSection = { ...section };
  for (const k of JOB_COMPANY_CANONICAL_BOOLEANS) {
    if (!(k in out)) {
      out[k] = DEFAULT_TRUE_JOB_COMPANY_KEY_SET.has(k);
    }
  }
  for (const k of JOB_COMPANY_CUSTOM_FIELD_KEYS) {
    if (!(k in out)) {
      out[k] = { label: '', enabled: false };
    }
  }
  return out;
}

function preserveJciKeyOrderFromApi(preFillKeyOrder: string[], withDefaults: RtiFieldSection): RtiFieldSection {
  const out: RtiFieldSection = {};
  for (const k of preFillKeyOrder) {
    if (k in withDefaults) {
      out[k] = withDefaults[k]!;
    }
  }
  for (const k of Object.keys(withDefaults)) {
    if (!(k in out)) {
      out[k] = withDefaults[k]!;
    }
  }
  return out;
}

function isCustomCompensationFieldKey(fieldKey: string): boolean {
  return /^custom_comp_field_\d+$/.test(fieldKey);
}

function isRtiJobCompanyCustomFieldKey(fieldKey: string): boolean {
  return /^custom_field_\d+$/.test(fieldKey);
}

function normalizeLeaf(fieldKey: string, raw: unknown): RtiFieldLeaf | null {
  if (typeof raw === 'boolean') return raw;
  if (raw === null) {
    if (isRtiJobCompanyCustomFieldKey(fieldKey)) {
      return { label: '', enabled: false };
    }
    return false;
  }
  if (isCustomFieldValue(raw)) {
    const o = raw as Record<string, unknown>;
    const rawLabel = typeof o.label === 'string' ? o.label : '';
    const label =
      rawLabel.length > 0
        ? rawLabel
        : isCustomCompensationFieldKey(fieldKey) || isRtiJobCompanyCustomFieldKey(fieldKey)
          ? ''
          : humanizeRtiFieldKey(fieldKey);
    const enabled = typeof o.enabled === 'boolean' ? o.enabled : false;
    return { label, enabled };
  }
  const loose = tryCoerceLooseRtiObjectLeaf(fieldKey, raw);
  if (loose) {
    return loose;
  }
  return null;
}

function normalizeFieldSection(raw: unknown): RtiFieldSection {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: RtiFieldSection = {};
  for (const [key, val] of Object.entries(raw)) {
    const normalized = normalizeLeaf(key, val);
    if (normalized !== null) {
      out[key] = normalized;
    }
  }
  return out;
}

export function defaultRtiComparisonGroup(): RtiComparisonGroupModel {
  const withDefaults = fillMissingJobCompanyLeaves({});
  return {
    job_company_information: preserveJciKeyOrderFromApi([], withDefaults),
  };
}

export function applyDefaultJobCompanyValuesPreservingKeyOrder(section: RtiFieldSection): RtiFieldSection {
  const defaults = defaultRtiComparisonGroup().job_company_information;
  const out: RtiFieldSection = {};
  for (const k of Object.keys(section)) {
    if (k in defaults) {
      out[k] = defaults[k]!;
    } else {
      out[k] = section[k]!;
    }
  }
  for (const k of Object.keys(defaults)) {
    if (!(k in out)) {
      out[k] = defaults[k]!;
    }
  }
  return out;
}

export function normalizeRtiComparisonGroup(raw: unknown): RtiComparisonGroupModel {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return defaultRtiComparisonGroup();
  }
  const d = raw as Record<string, unknown>;
  const jciRaw = d.job_company_information;
  const normalized =
    jciRaw && typeof jciRaw === 'object' && !Array.isArray(jciRaw) ? normalizeFieldSection(jciRaw) : {};
  const preFillKeyOrder = Object.keys(normalized);
  const withDefaults = fillMissingJobCompanyLeaves(normalized);
  const job_company_information = preserveJciKeyOrderFromApi(preFillKeyOrder, withDefaults);
  return { job_company_information };
}

export function getOrderedComparisonGroupJobCompanyEntries(
  section: RtiFieldSection,
): [string, RtiFieldLeaf][] {
  return Object.keys(section).map((k) => [k, section[k]!]);
}
